////////////////////////////////////////////
// WALLETCONNECT DEEPLINK — PER-WALLET MOBILE PAIRING (NATIVE BROWSER)
////////////////////////////////////////////
//
// MOBILE BROWSERS HAVE NO INJECTED PROVIDERS — AN INSTALLED WALLET APP
// (METAMASK ET AL) IS ONLY REACHABLE OVER WALLETCONNECT. ON MOBILE THE REOWN
// SHEET IS OFF (EvmProvider SETS showQrModal BY UA), SO CURATED WALLET ROWS
// PAIR HEADLESSLY: START THE CONNECT, CATCH display_uri, AND OPEN THE CHOSEN
// WALLET'S OWN /wc UNIVERSAL LINK. THE STANDARD WALLETCONNECT_DEEPLINK_CHOICE
// KEY IS SET SO LATER SIGN REQUESTS AUTO-OPEN THE SAME APP.
import type { Connector } from "wagmi";
import type { WalletBrand } from "@/lib/wallet/registry";

const CHOICE_KEY = "WALLETCONNECT_DEEPLINK_CHOICE";

// THE WALLETCONNECT EthereumProvider'S EVENT SURFACE (THE PART WE USE)
interface WcProvider {
  once(event: "display_uri", cb: (uri: string) => void): void;
  removeListener(event: "display_uri", cb: (uri: string) => void): void;
}

// WHICH WALLET APP THE CURRENT WC SESSION WAS PAIRED WITH — LETS THE UI TAG
// THE RIGHT BRAND ROW AS connected AND NAME THE APP IN THE ACCOUNT MENU
export function wcChoiceName(): string | null {

  try {

    const raw = localStorage.getItem(CHOICE_KEY);
    return raw ? ((JSON.parse(raw) as { name?: string }).name ?? null) : null;
  } catch {
    return null;
  }
}

// REMEMBER WHICH APP THIS PAIRING TARGETS — THE WC PROVIDER READS THE KEY TO
// AUTO-REOPEN THE APP ON LATER SIGN REQUESTS, AND THE UI READS IT TO BRAND
// THE SESSION
export function setWcChoice(brand: WalletBrand) {

  try {
    localStorage.setItem(CHOICE_KEY, JSON.stringify({
      name: brand.name,
      href: brand.wcLink!.replace(/wc\?uri=$/, ""),
    }));
  } catch { /* STORAGE BLOCKED — SIGN REQUESTS WON'T AUTO-OPEN THE APP */ }
}

// OPEN THE CHOSEN WALLET APP WITH A PAIRING URI — MUST RUN AS CLOSE TO THE
// USER GESTURE AS POSSIBLE (ANDROID ONLY LAUNCHES APP LINKS INSIDE ONE)
export function wcOpenWallet(brand: WalletBrand, uri: string) {

  setWcChoice(brand);
  location.assign(brand.wcLink + encodeURIComponent(uri));
}

// THE WAGMI CONNECTOR'S EVENT BUS — display_uri IS RE-EMITTED THERE AS A
// message EVENT; LISTENING ON BOTH SURFACES COVERS CONNECTOR-VERSION DRIFT
interface WcEmitter {
  on(event: "message", cb: (m: { type: string; data?: unknown }) => void): void;
  off(event: "message", cb: (m: { type: string; data?: unknown }) => void): void;
}

// PRE-PAIR — START A HEADLESS WALLETCONNECT PAIRING AND HAND BACK THE URI AS
// SOON AS THE RELAY MINTS IT. THE CONNECT MODAL CALLS THIS WHEN IT OPENS SO
// A WALLET ROW TAP CAN NAVIGATE SYNCHRONOUSLY WITH A READY URI.
export async function wcPreparePairing(opts: {
  connector: Connector;
  onUri: (uri: string) => void;
  connectAsync: (args: { connector: Connector }) => Promise<unknown>;
}): Promise<void> {

  let delivered = false;
  const deliver = (uri: string) => {

    if (delivered)
      return;
    delivered = true;
    opts.onUri(uri);
  };

  const provider = (await opts.connector.getProvider()) as WcProvider;
  const onProviderUri = (uri: string) => deliver(uri);
  const onMessage = (m: { type: string; data?: unknown }) => {

    if (m.type === "display_uri" && typeof m.data === "string") deliver(m.data);
  };
  const emitter = (opts.connector as unknown as { emitter?: WcEmitter }).emitter;

  provider.once("display_uri", onProviderUri);
  emitter?.on("message", onMessage);
  try {
    // RESOLVES WHEN A WALLET APPROVES THE SESSION; REJECTS ON ABORT/CANCEL
    await opts.connectAsync({ connector: opts.connector });
  } finally {

    provider.removeListener("display_uri", onProviderUri);
    emitter?.off("message", onMessage);
  }
}
